import { buildStudyDicomZip } from './buildStudyDicomZip';
import {
  buildStudyDicomZipFilename,
  triggerBrowserDownload,
} from './buildStudyDicomZipFilename';
import { classifyDownloadError } from './downloadError';
import { DOWNLOAD_MESSAGES } from './downloadMessages';
import {
  collectStudyDicomInstances,
  resolveStudyInstanceUIDFromViewport,
} from './resolveViewportStudy';
import type { DicomZipProgressCallback } from './types';

export type DownloadStudyDicomZipOptions = {
  viewportId: string;
  servicesManager: AppTypes.ServicesManager;
  onProgress?: DicomZipProgressCallback;
};

export type DownloadStudyDicomZipResult =
  | { success: true; filename: string; instanceCount: number }
  | { success: false; message: string };

export async function downloadStudyDicomZip({
  viewportId,
  servicesManager,
  onProgress,
}: DownloadStudyDicomZipOptions): Promise<DownloadStudyDicomZipResult> {
  const studyInstanceUID = resolveStudyInstanceUIDFromViewport(viewportId, servicesManager);

  if (!studyInstanceUID) {
    return { success: false, message: DOWNLOAD_MESSAGES.NO_DICOM_AVAILABLE };
  }

  const instances = collectStudyDicomInstances(studyInstanceUID);

  if (!instances.length) {
    return { success: false, message: DOWNLOAD_MESSAGES.NO_DICOM_AVAILABLE };
  }

  try {
    const { blob, includedCount } = await buildStudyDicomZip(instances, onProgress);

    if (!includedCount) {
      return { success: false, message: DOWNLOAD_MESSAGES.NO_DICOM_AVAILABLE };
    }

    const filename = buildStudyDicomZipFilename(studyInstanceUID, instances, servicesManager);
    triggerBrowserDownload(blob, filename);

    if (process.env.NODE_ENV !== 'production') {
      console.info('[ViewportDownload] DICOM ZIP export complete', {
        studyInstanceUID,
        filename,
        instanceCount: includedCount,
      });
    }

    return { success: true, filename, instanceCount: includedCount };
  } catch (error) {
    console.error('[ViewportDownload] DICOM ZIP export failed:', error);
    return { success: false, message: classifyDownloadError(error) };
  }
}
